import { createColumnHelper } from '@tanstack/react-table';
import { useState } from 'react';
import { Table, TableProvider, usePaginatedData } from '@optimus-web/core';
import { LocalizedText, useDialog, useIsTextKey, useUrlSyncedTableState } from '@mews/framework';
import { TextKey, RouteName } from '@mews-commander/enums';
import { UnicodeEntity } from '@mews/enums';
import {
    ExportScheduleFrequencyEnum,
    ExportTypeEnum,
    ReportTypeEnum,
    ScheduleItem,
    useRepositorySuspenseQuery,
} from '@mews-commander/data-access';
import { LocalizedDateTimeFromUtc } from '@mews-commander/ui';
import { Stack } from '@optimus-web/core/src/Stack';
import { IconButton } from '@optimus-web/core/src/IconButton';
import { IconName } from '@optimus-web/icons';
import { useCommanderThunkDispatch } from '@mews-commander/utils';
import { EditScheduleDialog } from '../EditScheduleDialog';
import { DeleteSchedule } from '../DeleteSchedule';
import { showRouteInModal } from '../../../app/actions/common';
import { usePayoutReportEditScheduledExportDialogForm } from '../../../payout/components/scheduledExport/usePayoutReportEditScheduledExportDialogForm';

export const useSchedules = () =>
    useRepositorySuspenseQuery('exports', 'searchSchedules', {}, { refetchOnMount: 'always' });

const columnHelper = createColumnHelper<ScheduleItem>();
interface ScheduleExportProps {
    exportName: string;
    exportType: ExportTypeEnum;
    frequency: ExportScheduleFrequencyEnum;
    isEnabled: boolean;
    notifyCreatorAboutExport: boolean;
    scheduleExportId: string;
    startUtc: string;
    scheduleExportType: ExportTypeEnum;
    reportType: ReportTypeEnum;
}

const FrequencyColumnCell = ({ getValue }: { getValue: () => ExportScheduleFrequencyEnum | undefined }) => {
    const isTextKey = useIsTextKey();
    const value = getValue();
    return value && isTextKey(ExportScheduleFrequencyEnum[value]) ? (
        <LocalizedText textKey={ExportScheduleFrequencyEnum[value]} />
    ) : (
        <>{UnicodeEntity.Dash}</>
    );
};

const getColumns = (onEditClick: (scheduleExportProps: ScheduleExportProps) => void) => [
    columnHelper.accessor('exportName', {
        header: () => <LocalizedText textKey={TextKey.ExportName} />,
    }),
    columnHelper.accessor('isEnabled', {
        header: () => <LocalizedText textKey={TextKey.Enabled} />,
        cell: (props) => <LocalizedText textKey={props.getValue() ? TextKey.Yes : TextKey.No} />,
    }),
    columnHelper.accessor('frequency', {
        header: () => <LocalizedText textKey={TextKey.Frequency} />,
        cell: ({ getValue }) => <FrequencyColumnCell getValue={getValue} />,
    }),
    columnHelper.accessor('startUtc', {
        header: () => <LocalizedText textKey={TextKey.Start} />,
        cell: (props) => <LocalizedDateTimeFromUtc utcString={props.getValue()?.toString() || ''} />,
    }),
    columnHelper.accessor('actions', {
        header: () => '',
        cell: (props) => {
            const itemProps = {
                exportName: props.row.original.exportName as string,
                exportType: props.row.original.exportType as ExportTypeEnum,
                frequency: props.row.original.frequency as ExportScheduleFrequencyEnum,
                isEnabled: props.row.original.isEnabled as boolean,
                notifyCreatorAboutExport: props.row.original.notifyCreatorAboutExport as boolean,
                scheduleExportId: props.row.original.scheduleExportId as string,
                startUtc: props.row.original.startUtc as string,
                scheduleExportType: props.row.original.exportType as ExportTypeEnum,
                reportType: props.row.original.reportType as ReportTypeEnum,
            };
            return (
                <Stack>
                    <IconButton
                        size="small"
                        variant="ghost"
                        onClick={() => onEditClick(itemProps)}
                        icon={IconName.Edit}
                        type="submit"
                    />
                    <DeleteSchedule
                        scheduleExportId={itemProps.scheduleExportId}
                        exportName={itemProps.exportName}
                        scheduleExportType={itemProps.scheduleExportType}
                        renderButtonComponent={(onClick) => (
                            <IconButton
                                variant="ghost"
                                icon={IconName.Delete}
                                onClick={onClick}
                                size="small"
                                type="submit"
                            />
                        )}
                    />
                </Stack>
            );
        },
        meta: { align: 'end' },
    }),
];

export const SchedulesTable = () => {
    const dispatch = useCommanderThunkDispatch();
    const { data, isFetching, refetch: refetchTableData } = useSchedules();
    const [{ active, onClose }, toggleExportDialogOpen] = useDialog();
    const [scheduleExportProps, setScheduleExportProps] = useState<ScheduleExportProps>();
    const [exportScheduleId, setExportScheduleId] = useState<string>('');
    const [renderPayoutReportEditDialog, togglePayoutReportEditDialog] = usePayoutReportEditScheduledExportDialogForm({
        exportScheduleId,
        refetchTableData,
    });
    const handleEditClick = (newScheduleExportProps: ScheduleExportProps) => {
        if (newScheduleExportProps.exportType === ExportTypeEnum.ReportExporter) {
            setScheduleExportProps(newScheduleExportProps);
            toggleExportDialogOpen();
        } else if (newScheduleExportProps.reportType === ReportTypeEnum.PayoutReport) {
            setExportScheduleId(newScheduleExportProps.scheduleExportId);
            togglePayoutReportEditDialog();
        } else {
            dispatch(showRouteInModal(RouteName.ExportScheduleDetail, { id: newScheduleExportProps.scheduleExportId }));
        }
    };
    const columns = getColumns(handleEditClick);
    const [tableState, setTableState] = useUrlSyncedTableState<ScheduleItem>({ columns });
    const paginatedData = usePaginatedData(data?.schedules || [], tableState.pagination);

    return (
        <>
            <TableProvider columns={columns} state={tableState} onStateChange={setTableState} as="div">
                <Table
                    amountOfRows={paginatedData.totalRowCount}
                    data={paginatedData.data}
                    fixedFirstColumn
                    fixedLastColumn
                    loading={isFetching}
                    uniqueRowId="scheduleExportId"
                />
            </TableProvider>
            {active ? <EditScheduleDialog {...(scheduleExportProps as ScheduleExportProps)} onClose={onClose} /> : null}
            {renderPayoutReportEditDialog()}
        </>
    );
};
